import { useEffect, useState } from 'react';
import { getOptimizedImageUrl } from '../lib/images';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    clinic_type: 'Klinik / Behandler',
    message: '',
    website_url: '' // Honeypot field
  });

  const [status, setStatus] = useState({
    submitting: false,
    submitted: false,
    error: null
  });

  useEffect(() => {
    document.title = 'Kontakt / Book en samtale - Health4media.dk';
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, submitted: false, error: null });

    // Client side validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim() || !formData.message.trim()) {
      setStatus({
        submitting: false,
        submitted: false,
        error: 'Udfyld venligst alle påkrævede felter.'
      });
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          clinic_type: formData.clinic_type,
          message: formData.message,
          website_url: formData.website_url // Mandatory honeypot
        })
      });

      if (!response.ok) {
        throw new Error('Der opstod en fejl ved afsendelse af formularen. Prøv venligst igen.');
      }

      setStatus({
        submitting: false,
        submitted: true,
        error: null
      });

      setFormData({
        name: '',
        phone: '',
        email: '',
        clinic_type: 'Klinik / Behandler',
        message: '',
        website_url: ''
      });
    } catch (err) {
      console.error('Submission error:', err);
      setStatus({
        submitting: false,
        submitted: false,
        error: err.message || 'Der opstod en uventet fejl. Kontakt os venligst direkte på health4media@gmail.com.'
      });
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      
      {/* HEADER HERO */}
      <section className="relative bg-slate-900 text-white py-10 sm:py-16 md:py-24 border-b border-slate-800 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/48b457c4-b6b2-4d62-b92b-6229aa267ddf/hero-modern-healthcare-consultation.png", { width: 1800, quality: 80 })}
            alt="Videoproduktion i sundhedsklinik"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-slate-950/80" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Book en samtale om klinikkens SoMe & videoredigering
            </h1>
            <p className="mt-3 sm:mt-4 text-sm sm:text-lg md:text-xl text-slate-300 leading-relaxed">
              Rådgivning, videoredigering og Meta postkalender til sundhedsklinikker. Altid med 0 binding og fleksible serviceaftaler.
            </p>
          </div>
        </div>
      </section>

      {/* FORM & INFO SECTION */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Left Contact Details & Reassurance */}
            <div className="lg:col-span-5 space-y-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
                  Vi svarer inden for 24 timer
                </h2>
                <p className="text-slate-600 mt-2 text-sm sm:text-base leading-relaxed">
                  Har du spørgsmål til hvordan online vejledning og redigering foregår i praksis, eller vil du have sparring på dine faglige idéer?
                </p>
              </div>

              {/* Direct contact info */}
              <div className="space-y-5">
                <div className="flex items-center gap-4">
                  <div className="text-xl text-sky-600 shrink-0">
                    <i className="fa-solid fa-phone" aria-hidden="true" />
                  </div>
                  <div>
                    <a 
                      href="tel:61489206" 
                      className="text-slate-900 font-bold hover:text-sky-700 transition text-sm sm:text-base no-wrap-contact"
                    >
                      61 48 92 06
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="text-xl text-sky-600 shrink-0">
                    <i className="fa-solid fa-envelope" aria-hidden="true" />
                  </div>
                  <div>
                    <a 
                      href="mailto:health4media@gmail.com" 
                      className="text-slate-900 font-bold hover:text-sky-700 transition text-sm sm:text-base no-wrap-contact"
                    >
                      health4media@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="text-xl text-sky-600 shrink-0 mt-0.5">
                    <i className="fa-solid fa-building text-sky-600" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-slate-900 font-semibold text-sm sm:text-base block">
                      Virksomhed
                    </span>
                    <span className="text-slate-600 text-sm">
                      Health4media · CVR: 46697553
                    </span>
                  </div>
                </div>


              </div>

              {/* Reassurance points */}
              <div className="pt-2 space-y-3">
                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Hvad sker der efter du sender?
                </h4>
                <ul className="space-y-2 text-xs text-slate-600">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                    <span>Vi læser din henvendelse og vender hurtigt tilbage</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                    <span>Vi aftaler en uforpligtende online samtale</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                    <span>Skræddersyet tilbud helt uden binding</span>
                  </li>
                </ul>
              </div>

            </div>

            {/* Right Booking / Contact Form */}
            <div className="lg:col-span-7">
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  Send en forespørgsel
                </h3>
                <p className="text-sm text-slate-600 mb-6">
                  Udfyld formularen for at starte samtalen om din kliniks videopositionering.
                </p>

                {status.submitted ? (
                  <div className="p-8 rounded-2xl bg-emerald-50 border border-emerald-200 text-center space-y-3 animate-in fade-in duration-300">
                    <div className="text-4xl text-emerald-600 mx-auto">
                      <i className="fa-solid fa-check" aria-hidden="true" />
                    </div>
                    <h4 className="text-xl font-bold text-emerald-900">
                      Mange tak for din henvendelse!
                    </h4>
                    <p className="text-sm text-emerald-800 max-w-md mx-auto">
                      Vi har modtaget dine oplysninger og vender tilbage til dig inden for 24 timer med forslag til en samtale.
                    </p>
                    <button
                      type="button"
                      onClick={() => setStatus({ submitting: false, submitted: false, error: null })}
                      className="mt-4 inline-block px-5 py-2 rounded-full bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-800 cursor-pointer"
                    >
                      Send en ny besked
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    
                    {/* Antispam honeypot (website_url) - absolutely sr-only */}
                    <div className="sr-only absolute opacity-0 -z-50 pointer-events-none" aria-hidden="true">
                      <label htmlFor="website_url">Website URL (leave empty)</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        tabIndex={-1}
                        autoComplete="off"
                        value={formData.website_url}
                        onChange={handleChange}
                      />
                    </div>

                    {status.error && (
                      <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs flex items-center gap-2">
                        <i className="fa-solid fa-circle-exclamation text-base" aria-hidden="true" />
                        <span>{status.error}</span>
                      </div>
                    )}

                    {/* Navn */}
                    <div>
                      <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                        Navn <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="F.eks. Læge Peter Hansen"
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition bg-slate-50/50"
                      />
                    </div>

                    {/* Telefon & Email Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                          Telefonnummer <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="F.eks. 12 34 56 78"
                          className="w-full px-4 py-3 rounded-xl border border-slate-300 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition bg-slate-50/50"
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="din@klinik.dk"
                          className="w-full px-4 py-3 rounded-xl border border-slate-300 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition bg-slate-50/50"
                        />
                      </div>
                    </div>

                    {/* Kliniktype */}
                    <div>
                      <label htmlFor="clinic_type" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                        Kliniktype / Branche
                      </label>
                      <select
                        id="clinic_type"
                        name="clinic_type"
                        value={formData.clinic_type}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition bg-slate-50/50"
                      >
                        <option value="Klinik / Behandler">Klinik / Behandler</option>
                        <option value="Hospital / Privathospital">Hospital / Privathospital</option>
                        <option value="Kiropraktisk Klinik">Kiropraktisk Klinik</option>
                        <option value="Fysioterapi / Træningscenter">Fysioterapi / Træningscenter</option>
                        <option value="Speciallægeklinik / Tandlæge">Speciallægeklinik / Tandlæge</option>
                        <option value="Anden sundhedsvirksomhed">Anden sundhedsvirksomhed</option>
                      </select>
                    </div>

                    {/* Beskrivelse */}
                    <div>
                      <label htmlFor="message" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                        Beskrivelse <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Fortæl kort om jeres klinik, nuværende synlighed eller hvad I ønsker at opnå med videoindhold..."
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition bg-slate-50/50"
                      />
                    </div>

                    {/* Submit Button */}
                    <div className="pt-2">
                      <button
                        type="submit"
                        disabled={status.submitting}
                        className="w-full inline-flex items-center justify-center gap-2.5 py-4 px-6 rounded-full bg-sky-600 hover:bg-sky-500 text-white font-bold text-base transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 cursor-pointer"
                      >
                        {status.submitting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            <span>Sender henvendelse...</span>
                          </>
                        ) : (
                          <>
                            <span>Book en samtale</span>
                            <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
                          </>
                        )}
                      </button>
                    </div>

                  </form>
                )}

              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
